import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Token as TokenIcon,
    AccountBalance as AccountBalanceIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { creditQAITokens } from '../../api/admin_api';

// Embedded theme
const theme = {
    colors: {
        bg: { primary: '#191b20', secondary: '#292b2f', tertiary: '#333539' },
        text: { primary: 'rgba(255,255,255,1)', secondary: 'rgba(255,255,255,0.9)', tertiary: 'rgba(255,255,255,0.7)', quaternary: 'rgba(255,255,255,0.5)' },
        accent: { primary: '#7c3aed', secondary: '#5b21b6', tertiary: '#a78bfa' },
        border: { primary: 'rgba(255,255,255,0.1)', secondary: 'rgba(255,255,255,0.05)' },
        status: { success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' }
    },
    gradients: { primaryAccent: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' },
    borderRadius: { sm: '8px', md: '12px' }
};

// Quick add amounts
const QUICK_AMOUNTS = [50, 100, 500, 1000];

const CreditTokensModal = ({ open, user, onClose, onSuccess }) => {
    const [amount, setAmount] = useState('');
    const [reference, setReference] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleQuickAmount = (value) => {
        setAmount(value.toString());
    };

    const handleSubmit = async () => {
        // Validation
        if (!amount || isNaN(amount) || parseInt(amount) <= 0) {
            setError('Please enter a valid token amount');
            return;
        }
        if (!reference.trim()) {
            setError('Reference is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await creditQAITokens(user.userId, parseInt(amount), reference.trim());

            if (result.success) {
                toast.success(`Successfully credited ${amount} tokens to ${user.email}`);
                // Reset form
                setAmount('');
                setReference('');
                onSuccess?.();
            } else {
                setError(result.error || 'Failed to credit tokens');
            }
        } catch (err) {
            console.error('Error crediting tokens:', err);
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setAmount('');
            setReference('');
            setError(null);
            onClose();
        }
    };

    if (!user) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: theme.colors.bg.primary,
                    border: `1px solid ${theme.colors.border.primary}`,
                    borderRadius: theme.borderRadius.md
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.colors.border.primary}`,
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TokenIcon sx={{ color: theme.colors.accent.tertiary }} />
                    <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        Credit Tokens
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ minWidth: 'auto', color: theme.colors.text.quaternary }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* User Info */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    background: theme.colors.bg.secondary,
                    borderRadius: theme.borderRadius.sm,
                    border: `1px solid ${theme.colors.border.primary}`
                }}>
                    <Typography sx={{ color: theme.colors.text.quaternary, fontSize: '0.75rem', mb: 0.5 }}>
                        Crediting tokens to:
                    </Typography>
                    <Typography sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        {user.email}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <AccountBalanceIcon sx={{ color: theme.colors.accent.tertiary, fontSize: 18 }} />
                        <Typography sx={{ color: theme.colors.text.secondary }}>
                            Current Balance: <strong>{user.tokenBalance?.toLocaleString() || 0}</strong> tokens
                        </Typography>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Amount Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Token Amount *
                </Typography>
                <TextField
                    fullWidth
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    InputProps={{
                        startAdornment: <TokenIcon sx={{ color: theme.colors.text.quaternary, mr: 1 }} />
                    }}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        }
                    }}
                />

                {/* Quick Add Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {QUICK_AMOUNTS.map((value) => (
                        <Chip
                            key={value}
                            label={`+${value}`}
                            onClick={() => handleQuickAmount(value)}
                            sx={{
                                cursor: 'pointer',
                                background: amount === value.toString()
                                    ? theme.colors.accent.primary
                                    : theme.colors.bg.tertiary,
                                color: theme.colors.text.primary,
                                fontWeight: 600,
                                '&:hover': {
                                    background: theme.colors.accent.secondary
                                }
                            }}
                        />
                    ))}
                </Box>

                {/* Reference Input */}
                <Typography sx={{ color: theme.colors.text.secondary, mb: 1, fontWeight: 500 }}>
                    Reference Note *
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="e.g., Manual top-up, Promo credit, Refund..."
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            background: theme.colors.bg.secondary,
                            color: theme.colors.text.primary,
                            '& fieldset': { borderColor: theme.colors.border.primary }
                        }
                    }}
                />

                {/* Preview */}
                {amount && parseInt(amount) > 0 && (
                    <Box sx={{
                        mt: 3,
                        p: 2,
                        background: theme.colors.status.success + '10',
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.success}30`
                    }}>
                        <Typography sx={{ color: theme.colors.status.success, fontWeight: 600 }}>
                            New Balance: {((user.tokenBalance || 0) + parseInt(amount)).toLocaleString()} tokens
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{
                p: 2,
                borderTop: `1px solid ${theme.colors.border.primary}`,
                gap: 1
            }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: theme.colors.text.tertiary }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !amount || !reference.trim()}
                    startIcon={loading ? <CircularProgress size={16} /> : <TokenIcon />}
                    sx={{
                        background: theme.gradients.primaryAccent,
                        '&:hover': { background: theme.colors.accent.secondary },
                        '&:disabled': { background: theme.colors.bg.tertiary }
                    }}
                >
                    {loading ? 'Crediting...' : `Credit ${amount || 0} Tokens`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreditTokensModal;
